import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {IDatabaseFilterQuery} from '@toolify/client/src/modules/databases/types/IDatabaseFilterQuery'
import {IViewFilterQuery} from '@toolify/server/src/repositories/ViewRepository/types/IViewFilterQuery'
import {
  ICreateViewRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseViewController/validators/CreateView/types/ICreateViewRequestInterfaces'
import {
  IGetViewsRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseViewController/validators/GetViews/types/IGetViewsRequestInterfaces'
import {
  IDeleteViewRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseViewController/validators/DeleteView/types/IDeleteViewRequestInterfaces'
import {
  IUpdateViewRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseViewController/validators/UpdateView/types/IUpdateViewRequestInterfaces'
import {mapDatabaseViewResponseInstance} from '@toolify/client/src/modules/api/singletons/mapDatabaseViewResponseInstance'
import {
  IGetViewRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseViewController/validators/GetView/types/IGetViewRequestInterfaces'
import {IUpdateDatabaseViewPositionRequestInterfaces} from '../DatabasePropertyApiStore/types/IUpdateDatabaseViewPositionRequestInterfaces'
import {
  IFileResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IFileResponseInstance.js'
import {
  IUploadViewPayloadFileRequestInterface,
} from '@toolify/server/src/controllers/DatabaseViewController/validators/UploadPayloadFile/IUploadViewPayloadFileRequestInterface.js'
import {
  IDuplicateViewRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseViewController/validators/DuplicateView/types/IDuplicateViewRequestInterfaces'

export const useDatabaseViewApiStore = defineStore('databaseViewApi', {
  actions: {
    async createView(
      databaseId: string,
      type: DatabaseViewType,
      name: string,
      filterQuery?: IDatabaseFilterQuery,
      position?: number,
      sourceDatabaseViewId?: string,
    ): Promise<ICreateViewRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {databaseView} = await apiRequestStore.post<ICreateViewRequestInterfaces>(`/database/${databaseId}/view`, {
        data: {
          name,
          type,
          filterQuery,
          position,
          sourceDatabaseViewId,
        },
      })
      return {
        databaseView: mapDatabaseViewResponseInstance(databaseView),
      }
    },

    async getView(
      databaseViewId: string,
    ): Promise<IGetViewRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {databaseView} = await apiRequestStore.get<IGetViewRequestInterfaces>(`/view/${databaseViewId}`)
      return {
        databaseView: mapDatabaseViewResponseInstance(databaseView),
      }
    },

    async updateView(databaseViewId: string, databaseView: IUpdateViewRequestInterfaces['body']): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.patch<IUpdateViewRequestInterfaces>(`/view/${databaseViewId}`, {
        data: databaseView,
      })
    },

    async updateViewPosition(databaseViewId: string, offset: number): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.patch<IUpdateDatabaseViewPositionRequestInterfaces>(`/view/${databaseViewId}/position`, {
        data: {
          offset,
        },
      })
    },

    async deleteView(databaseViewId: string): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.delete<IDeleteViewRequestInterfaces>(`/view/${databaseViewId}`)
    },

    async uploadPayloadFile(databaseViewId: string, file: File): Promise<IFileResponseInstance> {
      const apiRequestStore = useApiRequestStore()
      const formData = new FormData()
      formData.append('file', file)
      const {file: uploadedFile} = await apiRequestStore.post<IUploadViewPayloadFileRequestInterface>(`/view/${databaseViewId}/file`, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      })
      return uploadedFile
    },

    async getViews(databaseId: string): Promise<IGetViewsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {databaseViews} = await apiRequestStore.get<IGetViewsRequestInterfaces>(`/database/${databaseId}/views`)
      const mappedViews = databaseViews.map(databaseView => {
        return mapDatabaseViewResponseInstance(databaseView)
      })
      return {
        databaseViews: mappedViews,
      }
    },
    async duplicateView(
      databaseViewId: string,
      filterQuery?: IViewFilterQuery,
      hiddenFilterQuery?: IViewFilterQuery,
      sortedDatabasePropertyIds?: string[],
    ): Promise<IDuplicateViewRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const res = await apiRequestStore.post<IDuplicateViewRequestInterfaces>(`/view/${databaseViewId}/duplicate`, {
        data: filterQuery || hiddenFilterQuery || sortedDatabasePropertyIds?.length ? {
          filterQuery,
          hiddenFilterQuery,
          sortedDatabasePropertyIds,
        } : undefined,
      })
      return {
        jobId: res.jobId,
      }
    },
  },
})
